import { useState } from "react";
import { Link } from "wouter";
import { Shield, ArrowLeft, Heart, ChevronDown, ChevronUp } from "lucide-react";
import { LegalFooter } from "@/components/layout/legal-footer";
import { BrowserStorageCard } from "@/components/ui/browser-storage-card";

export default function PrivacyPolicy() {
  const [storageOpen, setStorageOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-500" />
            <span className="font-bold text-slate-800">RheumHub</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 space-y-10">
        <div className="flex items-start gap-4">
          <div className="bg-teal-50 p-3 rounded-2xl">
            <Shield className="h-8 w-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
            <p className="text-slate-500 mt-1">Effective date: May 2026 &nbsp;·&nbsp; Last reviewed: May 2026</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none space-y-8">

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">1. Who we are (Data Controller)</h2>
            <p className="text-slate-700 leading-relaxed">
              RheumHub is a UK rheumatology platform providing clinical tools for specialist nurses and plain-language guidance for patients. RheumHub is operated by <strong>Medantix Health Ltd</strong>, a company registered in England and Wales. The data controller for personal data processed through this platform is <strong>Medantix Health Ltd</strong>.
            </p>
            <p className="text-slate-700 leading-relaxed">
              If you have questions about how your data is handled, please contact us at: <strong>privacy@medantixhealth.co.uk</strong>
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 space-y-2">
              <p>
                <strong>ICO registration:</strong> Medantix Health Ltd is committed to registering with the Information Commissioner's Office (ICO) as a data controller before any live (non-pilot) deployment. See the{" "}
                <a href="https://ico.org.uk/for-organisations/ico-register/" target="_blank" rel="noopener noreferrer" className="underline">ICO public register</a> for details.
              </p>
              <p>
                <strong>Data Protection Officer (DPO):</strong> A DPO will be formally designated before RheumHub is made available to the general public. Until then, all data-protection enquiries should be directed to <strong>privacy@medantixhealth.co.uk</strong>.
              </p>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">2. What data we collect</h2>
            <p className="text-slate-700 leading-relaxed">We collect and process the following categories of data:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Account data:</strong> Email address and name provided when creating your account.</li>
              <li><strong>Conversation data:</strong> The text of your AI chat sessions with RheumHub. These are linked to your user account.</li>
              <li><strong>AI usage metadata:</strong> Technical logs of each AI inference request — including timestamps, response duration, and token counts. Message content is never duplicated in audit logs.</li>
              <li><strong>Session data:</strong> Secure session cookies used to authenticate you. These are essential for the platform to function.</li>
              <li><strong>Local preferences:</strong> Role selection (nurse/patient), AI consent flag, and notice dismissal flags stored in your browser's localStorage. These are not transmitted to our servers.</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <strong>Special category data:</strong> If you are a patient using RheumHub, the conversations you have with the AI may contain health information. This is special category data under UK GDPR Article 9. See section 3 for our lawful basis.
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">3. Lawful basis for processing</h2>
            <div className="space-y-4 text-slate-700">
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">For nurses (healthcare professionals):</h3>
                <p>We rely on <strong>legitimate interests</strong> (UK GDPR Article 6(1)(f)) to process account data and conversation data. Our legitimate interest is providing an evidence-based clinical decision support tool to registered rheumatology nurses. This processing supports patient safety and professional development and is not outweighed by your rights.</p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">For patients (health data — special category):</h3>
                <p>For processing health-related conversation content, we rely on <strong>Article 9(2)(h) UK GDPR</strong> — processing necessary for the provision of health or social care — combined with <strong>explicit consent</strong> obtained via the AI consent modal shown on first use of the chat feature.</p>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Authentication:</h3>
                <p>Processing of authentication data is necessary to perform the contract (access to the platform) under <strong>Article 6(1)(b)</strong>.</p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">4. Data retention</h2>
            <p className="text-slate-700 leading-relaxed">We retain your data for the following periods:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Patient conversations and messages:</strong> Between 30 and 180 days from last activity, depending on your chosen retention preference (default: 90 days). After this period data is permanently deleted. You may also opt to keep data until you delete it manually.</li>
              <li><strong>Nurse conversations and messages:</strong> 365 days from last activity, then permanently deleted.</li>
              <li><strong>AI audit logs:</strong> Deleted at the same time as the associated conversation data.</li>
              <li><strong>Account data:</strong> Retained until you delete your account.</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              You can delete your data at any time from your <Link href="/patient/settings" className="text-teal-700 underline">Settings</Link> page.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">5. Third parties and sub-processors</h2>
            <p className="text-slate-700 leading-relaxed">
              We share data only with the processors listed below. A Data Processing Agreement (DPA) is in place with each.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-slate-700 border-collapse">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="border border-slate-200 px-4 py-2 font-semibold">Processor</th>
                    <th className="border border-slate-200 px-4 py-2 font-semibold">Purpose</th>
                    <th className="border border-slate-200 px-4 py-2 font-semibold">Data transferred</th>
                    <th className="border border-slate-200 px-4 py-2 font-semibold">Privacy policy &amp; DPA documentation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Authentication provider</td>
                    <td className="border border-slate-200 px-4 py-2">Authentication &amp; identity management</td>
                    <td className="border border-slate-200 px-4 py-2">Email, name, session tokens</td>
                    <td className="border border-slate-200 px-4 py-2">
                      <a href="/privacy" class="text-teal-700 underline">Privacy policy</a>
                    </td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-200 px-4 py-2 font-medium">OpenAI (via Replit AI proxy)</td>
                    <td className="border border-slate-200 px-4 py-2">AI response generation</td>
                    <td className="border border-slate-200 px-4 py-2">Chat message content</td>
                    <td className="border border-slate-200 px-4 py-2">
                      <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">openai.com/privacy</a>. Messages are not used to train models under our usage tier.
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-slate-200 px-4 py-2 font-medium">Replit</td>
                    <td className="border border-slate-200 px-4 py-2">Cloud hosting &amp; AI proxy infrastructure</td>
                    <td className="border border-slate-200 px-4 py-2">All data at rest and in transit</td>
                    <td className="border border-slate-200 px-4 py-2">
                      <a href="https://replit.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">replit.com/privacy</a>. Servers may be in the US; Standard Contractual Clauses apply for international transfers.
                    </td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-200 px-4 py-2 font-medium">Resend</td>
                    <td className="border border-slate-200 px-4 py-2">Transactional email (e.g. data deletion confirmations)</td>
                    <td className="border border-slate-200 px-4 py-2">Email address</td>
                    <td className="border border-slate-200 px-4 py-2">
                      <a href="https://resend.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">resend.com/privacy</a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-700">We use Umami, a self-hosted, cookieless analytics tool that runs on our own server. It counts page views without collecting personal data, setting cookies, or sharing data with third parties.</p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">6. Cookies and localStorage</h2>
            <p className="text-slate-700 leading-relaxed">RheumHub uses:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li><strong>Session cookies:</strong> Essential for authentication. Cannot be disabled without preventing access to the platform.</li>
              <li><strong>localStorage:</strong> Used to store your role preference (<code>rheumhub_role</code>), AI consent flag (<code>rheumhub_ai_consent_v1</code>), and notice dismissal state. This data stays in your browser and is never sent to our servers.</li>
            </ul>
            <p className="text-slate-700">
              We do not use tracking or advertising cookies. Our self-hosted analytics (Umami) does not use cookies. Under the Privacy and Electronic Communications Regulations 2003 (PECR), the localStorage items above are strictly functional and do not require prior consent, though we provide a first-visit notice.
            </p>
          </section>

          <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setStorageOpen((o) => !o)}
              className="w-full flex items-center justify-between px-8 py-5 text-left hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-inset"
              aria-expanded={storageOpen}
              aria-controls="browser-storage-panel"
            >
              <div className="flex items-center gap-3">
                <div className="bg-teal-50 p-2 rounded-xl">
                  <Shield className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Your browser storage</h2>
                  <p className="text-sm text-slate-500 mt-0.5">See exactly what RheumHub has stored in your browser — and clear it instantly.</p>
                </div>
              </div>
              {storageOpen
                ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" />
                : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />}
            </button>
            {storageOpen && (
              <div id="browser-storage-panel" className="px-8 pb-8">
                <BrowserStorageCard
                  standalone
                  cardClassName="space-y-4"
                />
              </div>
            )}
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">7. Your rights under UK GDPR</h2>
            <p className="text-slate-700 leading-relaxed">
              You have the following rights regarding your personal data. To exercise any right, use the self-service tools in your Settings page, or email <strong>privacy@medantixhealth.co.uk</strong>. We will respond within one calendar month.
            </p>
            <ul className="list-disc list-inside space-y-3 text-slate-700">
              <li>
                <strong>Right of access (Article 15 — Subject Access Request):</strong> Request a copy of your data via the "Export my data" button in your Settings page.
              </li>
              <li>
                <strong>Right to erasure (Article 17 — "right to be forgotten"):</strong> Delete all your data via the "Delete all my data" button in your Settings page.
              </li>
              <li>
                <strong>Right to portability (Article 20):</strong> Your data export is provided in machine-readable JSON format.
              </li>
              <li>
                <strong>Right to rectification (Article 16):</strong> Contact us at privacy@medantixhealth.co.uk to request corrections to inaccurate data.
              </li>
              <li>
                <strong>Right to restrict processing (Article 18):</strong> You may ask us to restrict processing of your personal data — for example, while a dispute about accuracy or a legitimate-interests objection is being resolved. Contact privacy@medantixhealth.co.uk to exercise this right.
              </li>
              <li>
                <strong>Right to object (Article 21):</strong> Where processing is based on legitimate interests (Article 6(1)(f)), you may object at any time on grounds relating to your particular situation. We will cease processing unless we can demonstrate compelling legitimate grounds that override your interests, rights, and freedoms. Contact privacy@medantixhealth.co.uk.
              </li>
              <li>
                <strong>Right to withdraw consent:</strong> Where processing is based on your consent (patient health data), you may withdraw it at any time — this does not affect the lawfulness of processing before withdrawal. Delete your data via Settings or contact us.
              </li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">8. Data breach notification</h2>
            <p className="text-slate-700 leading-relaxed">
              In the event of a personal data breach, Medantix Health Ltd will:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Notify the <strong>ICO within 72 hours</strong> of becoming aware of a breach that poses a risk to individuals' rights and freedoms, as required by UK GDPR Article 33.</li>
              <li>Notify affected individuals <strong>without undue delay</strong> where the breach is likely to result in a high risk to their rights and freedoms (Article 34).</li>
              <li>Maintain an internal record of all breaches, including those not reported to the ICO, in accordance with Article 33(5).</li>
            </ul>
            <p className="text-slate-700">
              If you believe your data has been compromised, please contact <strong>privacy@medantixhealth.co.uk</strong> immediately.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">9. Supervisory authority</h2>
            <p className="text-slate-700 leading-relaxed">
              You have the right to lodge a complaint with the <strong>Information Commissioner's Office (ICO)</strong>, the UK data protection supervisory authority.
            </p>
            <p className="text-slate-700">
              ICO website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">ico.org.uk</a><br />
              ICO helpline: 0303 123 1113
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">10. AI and automated processing</h2>
            <p className="text-slate-700 leading-relaxed">
              RheumHub uses AI (OpenAI GPT models) to generate responses in chat sessions. This constitutes automated processing of your personal data. However, no automated decisions with legal or similarly significant effects are made about you. All AI responses are presented as informational support only, not clinical decisions.
            </p>
            <p className="text-slate-700">
              For nurses: the AI Governance framework is documented at <Link href="/nurse/ai-governance" className="text-teal-700 underline">/nurse/ai-governance</Link>.
            </p>
          </section>

        </div>

        <LegalFooter />
      </main>
    </div>
  );
}
